/**
 * Pure utility for detecting payroll date/employee conflicts.
 * No Firestore dependency — receives data, returns results.
 */

/**
 * Does a payroll-index run entry include this employee? Single source of the
 * employeeId-membership semantics for `runs[].employeeIds`, shared by findConflicts
 * (here) and dtrLockDerivation.deriveLockStatus so the two readers can't diverge on
 * string-vs-raw id coercion (PRD_DtrWriteModel F7).
 *
 * @param {object} run - an index run entry ({ employeeIds, ... })
 * @param {string|number} employeeId
 * @returns {boolean}
 */
export const runIncludesEmployee = (run, employeeId) => {
  const ids = run?.employeeIds || [];
  return ids.includes(String(employeeId)) || ids.includes(employeeId);
};

/**
 * Find conflicts between a new/current payroll run and existing runs in the index.
 *
 * Two runs conflict when they share at least one employee AND their date ranges overlap.
 * A run can be skipped by passing its own runId (useful when resuming a draft).
 *
 * @param {Array} existingRuns - Array of run entries from the payroll index
 * @param {string} newPeriodStart - YYYY-MM-DD start date of the new run
 * @param {string} newPeriodEnd   - YYYY-MM-DD end date of the new run
 * @param {string[]} newEmployeeIds - Employee IDs selected in the new run
 * @param {string|null} currentRunId - RunId to exclude (null for brand-new runs)
 * @returns {Array} Array of conflict objects, empty if no conflicts
 */
export const findConflicts = (existingRuns, newPeriodStart, newPeriodEnd, newEmployeeIds, currentRunId = null) => {
  if (!existingRuns || existingRuns.length === 0) return [];
  if (!newEmployeeIds || newEmployeeIds.length === 0) return [];

  const conflicts = [];

  for (const run of existingRuns) {
    // Skip the current run (when resuming a draft)
    if (currentRunId && run.runId === currentRunId) continue;

    // 1. Check date overlap — string comparison works for YYYY-MM-DD format
    const datesOverlap = run.periodStart <= newPeriodEnd && run.periodEnd >= newPeriodStart;
    if (!datesOverlap) continue;

    // 2. Check employee intersection via the shared membership helper (F7) — same
    //    coercion as deriveLockStatus, so the two readers of employeeIds can't drift.
    const overlappingEmployees = newEmployeeIds.filter(nid => runIncludesEmployee(run, nid));
    if (overlappingEmployees.length === 0) continue;

    // Conflict found
    conflicts.push({
      runId: run.runId,
      status: run.status,
      periodStart: run.periodStart,
      periodEnd: run.periodEnd,
      employees: overlappingEmployees,
    });
  }

  return conflicts;
};

/**
 * Build a map of employeeId → conflict info for easy lookup in the UI.
 *
 * @param {Array} conflicts - Output of findConflicts()
 * @returns {Object} Map of employeeId → { runId, status, periodStart, periodEnd }
 */
export const buildConflictMap = (conflicts) => {
  const map = {};

  for (const conflict of conflicts) {
    for (const empId of conflict.employees) {
      // Keep the first conflict found per employee (most relevant)
      if (!map[empId]) {
        map[empId] = {
          runId: conflict.runId,
          status: conflict.status,
          periodStart: conflict.periodStart,
          periodEnd: conflict.periodEnd,
        };
      }
    }
  }

  return map;
};
